/**
 * ScreenerRadar — efficacy spider chart comparing screeners on Win Rate,
 * Sharpe and Drawdown. Drawdown is inverted (lower = better → further out),
 * and Sharpe/Drawdown are normalized relative to the best screener in the
 * run, so the chart shows relative standing, not absolute levels.
 *
 * Props:
 *   rows  from analytics.screenerAggregates(windows)
 *         [{name, color, winRate, avgSharpe, avgDrawdown}]
 */

const AXES = [
  { key: 'winRate', label: 'Win Rate' },
  { key: 'sharpe', label: 'Sharpe' },
  { key: 'drawdown', label: 'Drawdown' },
];

const clamp01 = (x) => Math.max(0, Math.min(1, x));

function polar(cx, cy, r, axisIndex, n) {
  const deg = -90 + (axisIndex * 360) / n;
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export default function ScreenerRadar({ rows = [] }) {
  if (rows.length < 1) return null;

  const maxSharpe = Math.max(0.01, ...rows.map((r) => r.avgSharpe));
  const maxDd = Math.max(0.01, ...rows.map((r) => r.avgDrawdown));

  const norm = (r) => ({
    winRate: clamp01(r.winRate / 100),
    sharpe: clamp01(r.avgSharpe / maxSharpe),
    drawdown: clamp01(1 - r.avgDrawdown / maxDd),
  });

  const size = 240;
  const cx = size / 2, cy = size / 2 + 6, R = 78;
  const n = AXES.length;
  const rings = [0.25, 0.5, 0.75, 1];

  const ringPoints = (frac) =>
    AXES.map((_, i) => {
      const p = polar(cx, cy, R * frac, i, n);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    }).join(' ');

  return (
    <div className="flex flex-col gap-3">
      <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', maxWidth: 280, height: 'auto', display: 'block', margin: '0 auto' }}>
        {/* Grid rings */}
        {rings.map((frac) => (
          <polygon key={frac} points={ringPoints(frac)} fill="none" stroke="#2a2d3a" strokeWidth="1" strokeOpacity="0.2" />
        ))}
        {/* Spokes + axis labels */}
        {AXES.map((ax, i) => {
          const edge = polar(cx, cy, R, i, n);
          const lab = polar(cx, cy, R + 18, i, n);
          return (
            <g key={ax.key}>
              <line x1={cx} y1={cy} x2={edge.x} y2={edge.y} stroke="#2a2d3a" strokeWidth="1" strokeOpacity="0.25" />
              <text x={lab.x} y={lab.y} textAnchor="middle" dominantBaseline="middle" fill="#749094" fontSize="10" fontWeight="600">{ax.label}</text>
            </g>
          );
        })}
        {/* Screener polygons */}
        {rows.map((r) => {
          const nv = norm(r);
          const pts = AXES.map((ax, i) => {
            const p = polar(cx, cy, R * nv[ax.key], i, n);
            return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
          }).join(' ');
          return <polygon key={r.name} points={pts} fill={r.color} fillOpacity="0.12" stroke={r.color} strokeWidth="1.5" />;
        })}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs">
        {rows.map((r) => (
          <span key={r.name} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: r.color }} />
            <span className="text-muted-foreground truncate max-w-[150px]">{r.name}</span>
          </span>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground text-center">Sharpe &amp; Drawdown normalized to the best screener in this run. Drawdown inverted (outer = lower).</p>
    </div>
  );
}
